import apiClient from './client';
import { API_COMPANY_SETTINGS_ENDPOINT } from './config';

export interface QaqcThresholds {
  base_oil_density_ppg: number;
  water_density_ppg: number;
  hgs_sg: number;
  lgs_sg: number;
  le_bias_offset: number;
  le_low_residual_limit: number;
  le_high_residual_limit: number;
  le_absolute_low: number;
  le_absolute_high: number;
  ba_slope_vs_mud_weight: number;
  ba_intercept_vs_mud_weight: number;
  ba_max_clamp: number;
  active_ba_residual_low_limit: number;
  active_ba_residual_high_limit: number;
  effluent_ba_residual_low_limit: number;
  effluent_ba_residual_high_limit: number;
  return_ba_residual_low_limit: number;
  return_ba_residual_high_limit: number;
  ba_support_low_limit: number;
  ba_support_low_limit_return_discard: number;
  active_ba_support_high_limit: number;
  ls_effluent_ba_support_high_limit: number;
  hs_effluent_ba_support_high_limit: number;
  hs_suction_ba_support_high_limit: number;
  return_ba_support_high_limit: number;
  ba_support_density_offset: number;
  minimum_density_above_offset: number;
  ba_hgs_active_low_limit: number;
  ba_hgs_active_high_limit: number;
  ba_hgs_effluent_low_limit: number;
  ba_hgs_effluent_high_limit: number;
  ba_hgs_hs_suction_high_limit: number;
  ba_hgs_return_discard_high_limit: number;
  low_hgs_denominator_limit: number;
  component_closure_ok_limit: number;
  component_closure_rerun_limit: number;
  phase_closure_limit: number;
  mud_weight_warning_limit: number;
  mud_weight_rerun_limit: number;
  le_ba_le_residual_trigger: number;
  le_ba_ba_residual_trigger: number;
  ba_hgs_return_discard_low_limit: number;
  warning_band_fraction: number;
}

export type QaqcThresholdKey = keyof QaqcThresholds;

export const DEFAULT_QAQC_THRESHOLDS: QaqcThresholds = {
  base_oil_density_ppg: 7,
  water_density_ppg: 8.33,
  hgs_sg: 4.2,
  lgs_sg: 2.6,
  le_bias_offset: 3,
  le_low_residual_limit: -10,
  le_high_residual_limit: 15,
  le_absolute_low: 10,
  le_absolute_high: 75,
  ba_slope_vs_mud_weight: 8.1,
  ba_intercept_vs_mud_weight: -65,
  ba_max_clamp: 65,
  active_ba_residual_low_limit: -15,
  active_ba_residual_high_limit: 15,
  effluent_ba_residual_low_limit: -35,
  effluent_ba_residual_high_limit: 20,
  return_ba_residual_low_limit: -25,
  return_ba_residual_high_limit: 40,
  ba_support_low_limit: 4,
  ba_support_low_limit_return_discard: 2,
  active_ba_support_high_limit: 14,
  ls_effluent_ba_support_high_limit: 22,
  hs_effluent_ba_support_high_limit: 25,
  hs_suction_ba_support_high_limit: 20,
  return_ba_support_high_limit: 40,
  ba_support_density_offset: 8.6,
  minimum_density_above_offset: 1,
  ba_hgs_active_low_limit: 1.5,
  ba_hgs_active_high_limit: 5,
  ba_hgs_effluent_low_limit: 1,
  ba_hgs_effluent_high_limit: 45,
  ba_hgs_hs_suction_high_limit: 25,
  ba_hgs_return_discard_high_limit: 20,
  low_hgs_denominator_limit: 2,
  component_closure_ok_limit: 5,
  component_closure_rerun_limit: 10,
  phase_closure_limit: 1,
  mud_weight_warning_limit: 0.75,
  mud_weight_rerun_limit: 1.5,
  le_ba_le_residual_trigger: 5,
  le_ba_ba_residual_trigger: 7.5,
  ba_hgs_return_discard_low_limit: 1.2,
  warning_band_fraction: 0.15,
};

export interface CompanySettings {
  oil_percent_calibration: number;
  water_percent_calibration: number;
  qaqc_thresholds: QaqcThresholds;
}

export async function getCompanySettings(): Promise<CompanySettings> {
  const response = await apiClient.get<CompanySettings>(API_COMPANY_SETTINGS_ENDPOINT);
  return response.data;
}

export async function updateCompanySettings(
  settings: CompanySettings
): Promise<CompanySettings> {
  const response = await apiClient.patch<CompanySettings>(
    API_COMPANY_SETTINGS_ENDPOINT,
    settings
  );
  return response.data;
}
